import React, { useState, useEffect } from 'react';
import ContentService from '../../application/services/ContentService';
import LocalizationService from '../../application/services/LocalizationService';

interface HtmlContentProps {
  fileName: string;
  language?: string;
  className?: string;
}

const HtmlContent: React.FC<HtmlContentProps> = ({ 
  fileName, 
  language, 
  className = 'html-content' 
}) => {
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState<string>(language || LocalizationService.getCurrentLanguage());

  // Dil değişikliklerini algıla
  useEffect(() => {
    const handleLanguageChange = () => {
      const newLanguage = LocalizationService.getCurrentLanguage();
      if (newLanguage !== currentLanguage) {
        setCurrentLanguage(newLanguage);
      }
    };

    // i18next'in dil değişikliği olayını dinle
    const i18n = LocalizationService.getI18nInstance();
    i18n.on('languageChanged', handleLanguageChange);

    // Component unmount olduğunda event listener'ı kaldır
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [currentLanguage]);

  // HTML içinden ana içeriği çıkaran helper fonksiyon
  const extractMainContent = (htmlString: string): string => {
    try {
      // DOM parser oluştur
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');
      
      console.log('[HtmlContent] HTML ayrıştırıldı:', doc.title);
      
      // Container div içeriğini al (veya body içeriği)
      const container = doc.querySelector('.container');
      if (container) {
        console.log('[HtmlContent] Container div bulundu, içeriği alınıyor');
        return container.innerHTML;
      }
      
      // Container yoksa body içeriğini al
      if (doc.body) {
        console.log('[HtmlContent] Body içeriği alınıyor');
        return doc.body.innerHTML;
      }
      
      // Hiçbiri yoksa orjinal string'i döndür
      console.warn('[HtmlContent] HTML içeriği düzgün ayrıştırılamadı, orijinal içerik kullanılıyor');
      return htmlString;
    } catch (error) {
      console.error('[HtmlContent] HTML içeriği işlenirken hata:', error);
      return htmlString;
    }
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setIsLoading(true);
        setError(null);
        console.log(`[HtmlContent] İçerik yükleniyor: ${fileName}, dil: ${language || currentLanguage}`);
        
        // ContentService kullanarak HTML içeriğini yükle
        const html = await ContentService.loadHtmlContent(fileName, language || currentLanguage);
        
        if (!html || html.trim() === '') {
          console.error(`[HtmlContent] Boş HTML içeriği alındı: ${fileName}`);
          setError('İçerik yüklenemedi - boş içerik.');
        } else {
          console.log(`[HtmlContent] İçerik başarıyla yüklendi (${html.length} karakter)`);
          
          // HTML'i işle - sadece gerekli içeriği al
          const processedHtml = extractMainContent(html);
          console.log(`[HtmlContent] İşlenmiş içerik (${processedHtml.length} karakter)`);
          
          setContent(processedHtml);
        }
      } catch (err) {
        console.error('[HtmlContent] İçerik yüklenirken hata:', err);
        setError(`İçerik yüklenemedi: ${err instanceof Error ? err.message : 'Bilinmeyen hata'}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, [fileName, language, currentLanguage]);

  // Hiç içerik yoksa ve yükleme tamamlandıysa manuel olarak içerik oluştur
  useEffect(() => {
    if (!isLoading && !error && (!content || content.trim() === '')) {
      console.warn(`${fileName} için HTML içeriği boş, otomatik içerik gösteriliyor`);
      setContent(`
        <div>
          <h1>${fileName.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</h1>
          <p>İçerik henüz yüklenmedi. Lütfen daha sonra tekrar deneyin.</p>
        </div>
      `);
    }
  }, [content, isLoading, error, fileName]);

  if (isLoading) {
    return <div className="loading-content">Yükleniyor...</div>;
  }

  if (error) {
    return <div className="error-content">{error}</div>;
  }

  return (
    <div 
      className={className}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
};

export default HtmlContent; 